// src/components/presets/BannerMinimal.tsx
'use client'

import React from 'react'
import { motion } from 'framer-motion'
import { X } from 'lucide-react'
import type { BannerProps } from '../Banner'
import { getAnimationValues } from '../../utils/style-helpers'
import { getMinimalPositionStyles } from '../../utils/positioning'

export function BannerMinimal(props: BannerProps) {
  const { config, texts, onAccept, onDecline, onOpenSettings, onClose, accentColor, style } = props
  const { closeButton, showSettingsButton, showRejectLink, descriptionMode = 'short' } = config

  const animationValues = getAnimationValues(style.animation.direction)
  const description =
    descriptionMode === 'long'
      ? texts.longDescription || texts.description
      : texts.shortDescription || texts.description

  const isTop = style.position.includes('top')
  const containerStyles = getMinimalPositionStyles(isTop)
  const buttonBorderRadiusValue = style.borderRadius === 'sharp' ? '0' : '4px'

  return (
    <motion.div
      initial={style.animation.enabled ? animationValues.initial : { opacity: 1 }}
      animate={style.animation.enabled ? animationValues.animate : { opacity: 1 }}
      exit={style.animation.enabled ? animationValues.exit : { opacity: 0 }}
      transition={{ duration: style.animation.duration }}
      style={containerStyles}
    >
      <div
        style={{
          borderTop: isTop ? 'none' : `1px solid ${accentColor}`,
          borderBottom: isTop ? `1px solid ${accentColor}` : 'none',
          backgroundColor: style.background,
        }}
      >
        <div
          style={{
            maxWidth: '1280px',
            margin: '0 auto',
            padding: '16px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '24px',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <p
                style={{
                  fontSize: '14px',
                  color: '#111827',
                  fontWeight: 500,
                  marginBottom: '4px',
                }}
              >
                {texts.heading}
              </p>
              <p
                style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  margin: 0,
                }}
              >
                {description}
              </p>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexShrink: 0,
              }}
            >
              {showSettingsButton && (
                <button
                  onClick={onOpenSettings}
                  style={{
                    padding: '8px 16px',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#374151',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#111827')}
                  onMouseLeave={e => (e.currentTarget.style.color = '#374151')}
                >
                  {texts.settingsButton}
                </button>
              )}

              {showRejectLink && (
                <button
                  onClick={onDecline}
                  style={{
                    padding: '8px 16px',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#6b7280',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#374151')}
                  onMouseLeave={e => (e.currentTarget.style.color = '#6b7280')}
                >
                  {texts.declineButton}
                </button>
              )}

              <button
                onClick={onAccept}
                style={{
                  padding: '8px 20px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#ffffff',
                  backgroundColor: accentColor,
                  border: 'none',
                  borderRadius: buttonBorderRadiusValue,
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={e => (e.currentTarget.style.opacity = '0.9')}
                onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
              >
                {texts.acceptButton}
              </button>

              {closeButton && (
                <button
                  onClick={onClose}
                  style={{
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: buttonBorderRadiusValue,
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                  aria-label={texts.closeButton}
                >
                  <X style={{ width: '16px', height: '16px', color: '#9ca3af' }} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
